import { useCallback, useEffect, useMemo, useState } from 'react';
import { GlobalState } from '@leverege/ui-redux';
import { Users } from '@leverege/api-redux';
import { EMPTY_OBJ, TWO_FACTOR_ENROLL_STEPS, TwoFacterEnrollDefaultProps } from '../Constants.js';
import useObjectKeysMemoize from './useObjectKeysMemoize.js';
import { defaultGetTwoFactorSettings } from '../Util.js';
import useOnStateChange from './useOnChangeState.js';
export default function useTwoFactorEnroll(opts) {
  const {
    classNames = EMPTY_OBJ,
    variants: pVariants,
    options: pOptions,
    components: pComponents,
    getTwoFactorSettings = defaultGetTwoFactorSettings,
    auth,
    onAfterEnroll,
    onBackToLogin,
    ...restProps
  } = opts;
  const v = useObjectKeysMemoize(pVariants);
  const variants = useMemo(() => {
    return {
      ...TwoFacterEnrollDefaultProps.variants,
      ...v
    };
  }, [v]);
  const o = useObjectKeysMemoize(pOptions);
  const options = useMemo(() => {
    return {
      ...TwoFacterEnrollDefaultProps.options,
      ...o
    };
  }, [o]);
  const c = useObjectKeysMemoize(pComponents);
  const components = useMemo(() => {
    return {
      ...TwoFacterEnrollDefaultProps.components,
      ...c
    };
  }, [c]);
  const rest = useObjectKeysMemoize(restProps);
  const [error, setError] = useState();
  const [loading, setLoading] = useState();
  const [twoFactorSettings, setTwoFactorSettings] = useState();
  const [step, setStep] = useState(TWO_FACTOR_ENROLL_STEPS.OVERVIEW);
  const [enrollResponse, setEnrollResponse] = useState();
  const [oneTimeCodes, setOneTimeCodes] = useState();
  const [preActivateData, setPreActivateData] = useState();
  const [authenticationData, setAuthenticationData] = useState();
  const [confirmationData, setConfirmationData] = useState();
  const [selectedTwoFactorMethod, onChangeSelectedTwoFactorMethod] = useOnStateChange(null, true);
  useEffect(() => {
    if (!auth.requires2FAEnroll) {
      onBackToLogin();
      return;
    }
    getTwoFactorSettings().then(settings => {
      setTwoFactorSettings(settings);
    }).catch(err => {
      setError(err);
    });
  }, []);
  const onSelect2FAMethod = useCallback(() => {
    if (!selectedTwoFactorMethod.preActivateComponent && selectedTwoFactorMethod.onPreActivate) {
      selectedTwoFactorMethod.onPreActivate(auth.profile, preActivateData).then(res => {
        setAuthenticationData(res.data);
        setStep(TWO_FACTOR_ENROLL_STEPS.CONFIRM);
      });
    } else {
      setStep(TWO_FACTOR_ENROLL_STEPS.PRE_ACTIVATE);
    }
  }, [selectedTwoFactorMethod, preActivateData, auth.profile]);
  const onPreActivate = useCallback(() => {
    if (selectedTwoFactorMethod.onPreActivate) {
      setLoading(true);
      selectedTwoFactorMethod.onPreActivate(auth.profile, preActivateData).then(res => {
        setAuthenticationData(res.data);
        setStep(TWO_FACTOR_ENROLL_STEPS.CONFIRM);
        setLoading(false);
      });
    }
  }, [selectedTwoFactorMethod, auth, preActivateData]);
  const onConfirm2FAMethod = useCallback(() => {
    setLoading(true);
    GlobalState.dispatch(Users.activateTwoFactorMethod(auth.profile.id, selectedTwoFactorMethod.type, confirmationData?.code)).then(res => {
      setOneTimeCodes(res.oneTimeCodes);
      setStep(TWO_FACTOR_ENROLL_STEPS.ONE_TIME_CODES);
      setLoading(false);
      setEnrollResponse(res);
    });
  }, [auth, selectedTwoFactorMethod, confirmationData]);
  const onBackToMethodSelect = useCallback(() => {
    setStep(TWO_FACTOR_ENROLL_STEPS.SELECT_METHOD);
  }, []);
  return useMemo(() => {
    const onNext = step === TWO_FACTOR_ENROLL_STEPS.OVERVIEW ? () => {
      setStep(TWO_FACTOR_ENROLL_STEPS.SELECT_METHOD);
    } : () => {
      setLoading(true);
      onAfterEnroll?.(enrollResponse);
    };
    return {
      classNames,
      components,
      variants,
      options,
      ...rest,
      auth,
      state: {
        authenticationData,
        confirmationData,
        error,
        loading,
        onBackToMethodSelect,
        onChangeSelectedTwoFactorMethod,
        onConfirm2FAMethod,
        oneTimeCodes,
        onNext,
        onPreActivate,
        onSelect2FAMethod,
        selectedTwoFactorMethod,
        setConfirmationData,
        setPreActivateData,
        step,
        twoFactorSettings
      }
    };
  }, [auth, authenticationData, classNames, components, confirmationData, enrollResponse, error, loading, onAfterEnroll, onBackToMethodSelect, onChangeSelectedTwoFactorMethod, onConfirm2FAMethod, oneTimeCodes, onPreActivate, onSelect2FAMethod, options, rest, selectedTwoFactorMethod, setConfirmationData, setPreActivateData, step, twoFactorSettings, variants]);
}